import { Routes } from '@angular/router';
import { authGuard, guestGuard } from '@core/auth/guards/auth.guard/auth.guard';
import { initErrorGuard, initErrorPageGuard } from '@core/guards/init-error.guard/init-error.guard';
import { PATHS } from './app-routes.consts';

/** Route `title` values are translation keys (see TranslatedTitleStrategy). */
export const routes: Routes = [
  {
    // No `title`: shown when translations may have failed to load, so a
    // translation key would surface raw (index.html's title stays).
    path: 'init-error',
    canActivate: [initErrorPageGuard],
    loadComponent: () =>
      import('@features/init-error/init-error-page/init-error-page').then((m) => m.InitErrorPage),
  },
  {
    path: 'auth',
    canActivate: [initErrorGuard, guestGuard],
    loadComponent: () => import('@layouts/auth-layout/auth-layout').then((m) => m.AuthLayout),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'login' },
      {
        path: 'login',
        title: 'auth.login.title',
        loadComponent: () => import('@features/auth/login/login-page/login-page').then((m) => m.LoginPage),
      },
    ],
  },
  {
    // Open CMS pages (privacy policy etc.) — no auth, no init-error guard:
    // they have to render even when the app itself could not start.
    path: `${PATHS.PUBLIC_PAGES}/:pageType`,
    loadComponent: () => import('@features/public-page/public-page').then((m) => m.PublicPage),
  },
  {
    path: `${PATHS.PUBLIC_PAGES}/:pageType/:language`,
    loadComponent: () => import('@features/public-page/public-page').then((m) => m.PublicPage),
  },
  {
    path: 'no-permissions',
    title: 'noPermissions.title',
    canActivate: [initErrorGuard],
    loadComponent: () =>
      import('@features/no-permissions/no-permissions-page').then((m) => m.NoPermissionsPage),
  },
  {
    // Explicit address, as in legacy — screens redirect here on a bad id.
    path: PATHS.ERROR_404,
    title: 'notFound.title',
    loadComponent: () =>
      import('@features/not-found/not-found-page').then((m) => m.NotFoundPage),
  },
  {
    /*
     * The application shell sits on the empty path — screens are addressed
     * `/clients/list`, without a prefix, exactly as legacy addresses them and
     * as the backend links to them. Because an empty path matches every URL as
     * a prefix, this entry has to come AFTER the standalone pages above
     * (`auth`, `public-pages`, `404`, …); otherwise every one of them would
     * first be tried as a child of the shell.
     */
    path: '',
    canActivate: [initErrorGuard, authGuard],
    loadComponent: () => import('@layouts/main-layout/main-layout/main-layout').then((m) => m.MainLayout),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        // Legacy key; the menu one has the same text but this is the page title.
        title: 'pageTitle.dashboard',
        /*
         * The dashboard is the shell's root: legacy serves it from the empty
         * path, and its builder skips empty segments, so the trail comes out
         * empty and the whole strip is hidden. Ours sits at a real segment, so
         * it has to say so — otherwise the pulpit grows a crumb pointing where
         * the home icon beside it already points, and the strip pushes the view
         * 66px down against legacy.
         */
        // `hideTitle` osobno od `isBreadcrumbRoot`: legacy chowa nagłówek na 20
        // trasach, a okruszki gubi tylko tutaj — to dwie różne decyzje, które
        // akurat na pulpicie spotykają się na jednej trasie.
        data: { isBreadcrumbRoot: true, hideTitle: true },
        loadComponent: () =>
          import('@features/dashboard/dashboard-page/dashboard-page').then((m) => m.DashboardPage),
      },
      {
        path: 'ui-guide',
        loadChildren: () =>
          import('@features/ui-guide/ui-guide.routes').then((m) => m.UI_GUIDE_ROUTES),
      },
      {
        path: PATHS.USERS,
        title: 'pageTitle.users',
        loadChildren: () => import('@features/users/users.routes').then((m) => m.USERS_ROUTES),
      },
      {
        path: PATHS.CLIENTS,
        title: 'pageTitle.clients',
        loadChildren: () =>
          import('@features/clients/clients.routes').then((m) => m.CLIENTS_ROUTES),
      },
      {
        path: PATHS.CUSTOM_PAGES,
        title: 'pageTitle.customPages',
        loadChildren: () =>
          import('@features/custom-pages/custom-pages.routes').then((m) => m.CUSTOM_PAGES_ROUTES),
      },
      {
        path: PATHS.REGULATIONS,
        title: 'pageTitle.regulations',
        loadChildren: () =>
          import('@features/regulations/regulations.routes').then((m) => m.REGULATIONS_ROUTES),
      },
      {
        path: PATHS.OFFERS,
        title: 'pageTitle.offers',
        loadChildren: () => import('@features/offers/offers.routes').then((m) => m.OFFERS_ROUTES),
      },
      {
        path: PATHS.ORDERS,
        title: 'pageTitle.orders',
        loadChildren: () => import('@features/orders/orders.routes').then((m) => m.ORDERS_ROUTES),
      },
      {
        path: PATHS.GROUPS,
        /*
         * „Oferty", nie „Grupy" — grupy należą do modułu ofert i legacy nadaje
         * tej trasie nadrzędnej `pageTitle.offers`, przez co okruszki czytają
         * się „Oferty > Grupy". Ten sam klucz co tutaj dawał „Grupy > Grupy".
         */
        title: 'pageTitle.offers',
        loadChildren: () =>
          import('@features/groups/groups.routes').then((m) => m.GROUPS_ROUTES),
      },
      {
        path: PATHS.LANGUAGE_SUPPORT,
        title: 'pageTitle.languages',
        loadChildren: () =>
          import('@features/language-support/language-support.routes').then(
            (m) => m.LANGUAGE_SUPPORT_ROUTES,
          ),
      },
      {
        path: PATHS.FAQ,
        title: 'pageTitle.faq',
        loadChildren: () => import('@features/faq/faq.routes').then((m) => m.FAQ_ROUTES),
      },
      {
        path: PATHS.INVOICES,
        title: 'pageTitle.invoices',
        loadChildren: () =>
          import('@features/invoices/invoices.routes').then((m) => m.INVOICES_ROUTES),
      },
      {
        path: PATHS.FILES,
        /*
         * „Dokumenty", nie „Szablony" — legacy nadaje tej trasie nadrzędnej
         * `pageTitle.files`, przez co okruszki czytają się „Dokumenty > Szablony".
         * Ten sam klucz co u dziecka dawał „Szablony > Szablony" (ta sama pomyłka
         * co przy grupach, poz. 28).
         */
        title: 'pageTitle.files',
        loadChildren: () =>
          import('@features/document-templates/document-templates.routes').then(
            (m) => m.DOCUMENT_TEMPLATES_ROUTES,
          ),
      },
      {
        path: PATHS.SETTINGS,
        title: 'pageTitle.settings',
        loadChildren: () =>
          import('@features/cms-settings/cms-settings.routes').then((m) => m.CMS_SETTINGS_ROUTES),
      },
      {
        path: PATHS.PRODUCTS,
        title: 'pageTitle.products',
        loadChildren: () =>
          import('@features/products/products.routes').then((m) => m.PRODUCTS_ROUTES),
      },
      {
        path: 'demo',
        loadChildren: () => import('@features/demo/demo.routes').then((m) => m.DEMO_ROUTES),
      },
    ],
  },
  {
    path: '**',
    title: 'notFound.title',
    canActivate: [initErrorGuard],
    loadComponent: () =>
      import('@features/not-found/not-found-page').then((m) => m.NotFoundPage),
  },
];
